import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    Link,
    Pagination,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import HearingDisabledOutlinedIcon from '@mui/icons-material/HearingDisabledOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';

import {
    getVoicePlaybackIssues,
    type VoicePlaybackIssue,
    type VoicePlaybackIssuePage,
} from '../admin/adminVoicePlaybackIssuesApi';

const pageSize = 25;

function formatDate(value: string): string {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function availableVoices(item: VoicePlaybackIssue): string[] {
    try {
        const parsed = JSON.parse(item.availableVoiceLabelsJson);
        return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === 'string') : [];
    } catch {
        return [];
    }
}

function QuestionCell({ item }: { item: VoicePlaybackIssue }) {
    return (
        <Stack spacing={0.4}>
            <Link component={RouterLink} to={`/live-exam-task-by-id/${item.examTaskId}`} underline="hover" fontWeight={800}>
                {item.questionNo || item.displayTitle || item.examTaskId}
            </Link>
            {item.questionNo && item.displayTitle ? (
                <Typography variant="caption" color="text.secondary">{item.displayTitle}</Typography>
            ) : null}
            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                {item.examTaskType ? <Chip size="small" variant="outlined" label={item.examTaskType} /> : null}
                {item.examName ? <Chip size="small" label={item.examName} /> : null}
            </Stack>
        </Stack>
    );
}

function VoiceRoute({ item }: { item: VoicePlaybackIssue }) {
    return (
        <Stack spacing={0.3}>
            <Typography variant="body2" fontWeight={800}>{item.preferredVoiceLabel}</Typography>
            <Typography variant="caption" color="text.secondary">
                Fallback: {item.fallbackVoiceLabel || 'Another available voice'}
            </Typography>
        </Stack>
    );
}

function MobileIssueCard({ item }: { item: VoicePlaybackIssue }) {
    const voices = availableVoices(item);
    return (
        <Card variant="outlined">
            <CardContent>
                <Stack spacing={1.5}>
                    <QuestionCell item={item} />
                    <Divider />
                    <VoiceRoute item={item} />
                    <Typography variant="body2" color="text.secondary">
                        Available: {voices.length ? voices.join(', ') : 'No labelled voice'}
                    </Typography>
                    <Typography variant="body2">
                        {item.userFullName || item.userEmail || item.userId}
                    </Typography>
                    <Stack direction="row" justifyContent="space-between" gap={1}>
                        <Chip size="small" color="warning" label={`${item.occurrenceCount} occurrence${item.occurrenceCount === 1 ? '' : 's'}`} />
                        <Typography variant="caption" color="text.secondary">Last: {formatDate(item.lastSeenAt)}</Typography>
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
}

export function VoicePlaybackIssuesPage() {
    const [page, setPage] = useState(1);
    const [searchText, setSearchText] = useState('');
    const [search, setSearch] = useState('');
    const [result, setResult] = useState<VoicePlaybackIssuePage | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setResult(await getVoicePlaybackIssues({ page, pageSize, search }));
        } catch (requestError) {
            setError(requestError instanceof Error ? requestError.message : 'Could not load voice playback issues.');
        } finally {
            setLoading(false);
        }
    }, [page, search]);

    useEffect(() => {
        void load();
    }, [load]);

    const pageCount = useMemo(
        () => Math.max(1, Math.ceil((result?.totalCount ?? 0) / pageSize)),
        [result?.totalCount],
    );

    function applySearch(event: FormEvent) {
        event.preventDefault();
        setPage(1);
        setSearch(searchText.trim());
    }

    const summary = result?.summary;
    const summaryCards = [
        ['Issue rows', summary?.totalIssues ?? 0],
        ['Fallback occurrences', summary?.totalOccurrences ?? 0],
        ['Affected questions', summary?.affectedQuestions ?? 0],
        ['Affected users', summary?.affectedUsers ?? 0],
    ] as const;

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1700, mx: 'auto' }}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2} mb={3}>
                <Box>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <HearingDisabledOutlinedIcon color="warning" />
                        <Typography variant="h4" fontWeight={800}>Voice Playback Issues</Typography>
                    </Stack>
                    <Typography color="text.secondary" sx={{ mt: 0.75, maxWidth: 900 }}>
                        Questions where a student’s preferred voice was unavailable and the app played a fallback voice.
                    </Typography>
                </Box>
                <Tooltip title="Refresh">
                    <span>
                        <IconButton onClick={() => void load()} disabled={loading} color="primary">
                            {loading ? <CircularProgress size={22} /> : <RefreshIcon />}
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2.5 }}>
                {summaryCards.map(([label, value]) => (
                    <Card key={label} variant="outlined">
                        <CardContent>
                            <Typography variant="h5" fontWeight={800}>{value.toLocaleString()}</Typography>
                            <Typography variant="body2" color="text.secondary">{label}</Typography>
                        </CardContent>
                    </Card>
                ))}
            </Box>

            <Card variant="outlined">
                <CardContent>
                    <Stack component="form" onSubmit={applySearch} direction={{ xs: 'column', sm: 'row' }} spacing={1.25} mb={2}>
                        <TextField
                            size="small"
                            value={searchText}
                            onChange={(event) => setSearchText(event.target.value)}
                            placeholder="Search question, user, or voice"
                            sx={{ flex: 1, minWidth: 240 }}
                        />
                        <Button type="submit" variant="contained" startIcon={<SearchIcon />}>Search</Button>
                        {search ? (
                            <Button onClick={() => { setSearchText(''); setSearch(''); setPage(1); }}>Clear</Button>
                        ) : null}
                    </Stack>

                    {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
                    {!loading && !error && result?.items.length === 0 ? (
                        <Alert severity="success">No preferred-voice fallback issues found.</Alert>
                    ) : null}

                    <Stack spacing={1.25} sx={{ display: { xs: 'flex', md: 'none' } }}>
                        {(result?.items ?? []).map((item) => <MobileIssueCard key={item.voicePlaybackIssueId} item={item} />)}
                    </Stack>

                    <TableContainer sx={{ display: { xs: 'none', md: 'block' }, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Question</TableCell>
                                    <TableCell>Preferred → fallback</TableCell>
                                    <TableCell>Available voices</TableCell>
                                    <TableCell>User</TableCell>
                                    <TableCell align="right">Occurrences</TableCell>
                                    <TableCell>Seen</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(result?.items ?? []).map((item) => {
                                    const voices = availableVoices(item);
                                    return (
                                        <TableRow key={item.voicePlaybackIssueId} hover>
                                            <TableCell sx={{ minWidth: 230 }}><QuestionCell item={item} /></TableCell>
                                            <TableCell sx={{ minWidth: 180 }}><VoiceRoute item={item} /></TableCell>
                                            <TableCell sx={{ minWidth: 200 }}>
                                                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                                                    {voices.length ? voices.map((voice) => <Chip key={voice} size="small" variant="outlined" label={voice} />) : '—'}
                                                </Stack>
                                            </TableCell>
                                            <TableCell sx={{ minWidth: 180 }}>
                                                <Typography variant="body2" fontWeight={700}>{item.userFullName || item.userEmail || 'User'}</Typography>
                                                <Typography variant="caption" color="text.secondary">{item.userEmail || item.userId}</Typography>
                                            </TableCell>
                                            <TableCell align="right"><Chip size="small" color="warning" label={item.occurrenceCount} /></TableCell>
                                            <TableCell sx={{ minWidth: 190 }}>
                                                <Typography variant="body2">Last: {formatDate(item.lastSeenAt)}</Typography>
                                                <Typography variant="caption" color="text.secondary">First: {formatDate(item.firstSeenAt)}</Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {result && result.totalCount > pageSize ? (
                        <Stack alignItems="center" mt={2.5}>
                            <Pagination page={page} count={pageCount} onChange={(_, value) => setPage(value)} color="primary" />
                        </Stack>
                    ) : null}
                </CardContent>
            </Card>
        </Box>
    );
}
